<template>
  <div class="midpay-collect-form">
    <v-card class="payment-card" elevation="0">
      <v-card-title class="text-h5 pb-4">
        <v-icon left color="primary">mdi-credit-card</v-icon>
        Payment Information
      </v-card-title>

      <v-card-text>
        <v-alert v-if="errorMessage" type="error" dismissible @input="errorMessage = ''">
          {{ errorMessage }}
        </v-alert>

        <v-alert v-if="successMessage" type="success" dismissible>
          {{ successMessage }}
        </v-alert>

        <v-alert v-if="isTestMode" type="warning" text dense class="mb-4">
          <strong>🧪 TEST MODE</strong> - MidPayGateway not configured. Payments will be simulated.
          <br><small>Configure VUE_APP_MIDPAY_TOKENIZATION_KEY in client/.env for real payments.</small>
        </v-alert>

        <form id="midpay-payment-form">
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="billingInfo.firstName"
                label="First Name"
                outlined
                dense
                required
                :disabled="processing"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="billingInfo.lastName"
                label="Last Name"
                outlined
                dense
                required
                :disabled="processing"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="billingInfo.address"
                label="Billing Address"
                outlined
                dense
                required
                :disabled="processing"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="billingInfo.city"
                label="City"
                outlined
                dense
                required
                :disabled="processing"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field
                v-model="billingInfo.state"
                label="State"
                outlined
                dense
                required
                maxlength="2"
                :disabled="processing"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field
                v-model="billingInfo.zip"
                label="ZIP Code"
                outlined
                dense
                required
                :disabled="processing"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="billingInfo.country"
                label="Country"
                outlined
                dense
                required
                :disabled="processing"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="billingInfo.phone"
                label="Phone"
                outlined
                dense
                required
                :disabled="processing"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Card Information Section -->
          <v-divider class="my-4"></v-divider>
          <div class="text-subtitle-1 font-weight-medium mb-3">
            <v-icon left color="primary">mdi-credit-card-outline</v-icon>
            Card Information
          </div>

          <div class="card-fields-container">
            <!-- Test Mode: Show styled fake card fields -->
            <div v-if="isTestMode">
              <v-text-field
                v-model="testCardNumber"
                label="Card Number"
                outlined
                dense
                readonly
                :prepend-inner-icon="getCardIcon"
                hint="Test card for simulation"
                persistent-hint
                class="card-field-test"
              ></v-text-field>
              
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model="testCardExpiry"
                    label="Expiration Date"
                    outlined
                    dense
                    readonly
                    placeholder="MM / YY"
                    hint="Test expiration"
                    persistent-hint
                    class="card-field-test"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="testCardCvv"
                    label="CVV"
                    outlined
                    dense
                    readonly
                    hint="Test CVV"
                    persistent-hint
                    class="card-field-test"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>
            
            <!-- Production Mode: Real CollectJS -->
            <div v-else>
              <v-alert v-if="!collectJsLoaded" type="info" text dense class="mb-4">
                <v-progress-circular indeterminate size="20" width="2" color="primary" class="mr-2"></v-progress-circular>
                <small>Loading secure payment form...</small>
              </v-alert>
              <v-alert v-else type="info" text dense class="mb-4">
                <small>Your credit card information is securely processed by our payment partner.</small>
              </v-alert>
              <!-- CollectJS iframe will be injected here -->
              <div id="collect-card-fields"></div>
            </div>
          </div>
        </form>

        <div class="payment-summary mt-4 pa-4" style="background: #f5f5f5; border-radius: 8px;">
          <div class="d-flex justify-space-between mb-2">
            <span><strong>Amount Due:</strong></span>
            <span class="text-h6 primary--text">${{ amount.toFixed(2) }}</span>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="px-6 pb-6">
        <v-btn
          text
          @click="$emit('cancel')"
          :disabled="processing"
        >
          Cancel
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          large
          :loading="processing"
          :disabled="!isFormValid || processing || (!collectJsLoaded && !isTestMode)"
          @click="submitPayment"
        >
          <v-icon left>mdi-lock</v-icon>
          <span v-if="isTestMode">🧪 Simulate Payment ${{ amount.toFixed(2) }}</span>
          <span v-else-if="!collectJsLoaded">Loading Payment System...</span>
          <span v-else>Process Payment ${{ amount.toFixed(2) }}</span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'MidPayCollectForm',
  
  props: {
    enrollmentId: {
      type: String,
      required: true
    },
    amount: {
      type: Number,
      required: true
    },
    // Pre-fill billing info from enrollment
    enrollmentData: {
      type: Object,
      default: () => ({})
    }
  },
  
  data() {
    return {
      billingInfo: {
        firstName: '',
        lastName: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        country: 'US',
        phone: ''
      },
      processing: false,
      errorMessage: '',
      successMessage: '',
      collectJsLoaded: false,
      // Test card data
      testCardNumber: '4111 1111 1111 1111',
      testCardExpiry: '12/25',
      testCardCvv: '999'
    };
  },
  
  computed: {
    isFormValid() {
      return (
        this.billingInfo.firstName &&
        this.billingInfo.lastName &&
        this.billingInfo.address &&
        this.billingInfo.city &&
        this.billingInfo.state &&
        this.billingInfo.zip &&
        this.billingInfo.country &&
        this.billingInfo.phone
      );
    },
    
    isTestMode() {
      const tokenKey = process.env.VUE_APP_MIDPAY_TOKENIZATION_KEY;
      return !tokenKey || tokenKey === 'your-token-key-here';
    },
    
    getCardIcon() {
      // Detect card type from test card number
      if (this.testCardNumber.startsWith('4')) {
        return 'mdi-credit-card'; // Visa
      }
      return 'mdi-credit-card';
    }
  },
  
  mounted() {
    this.prefillBillingInfo();
    this.loadCollectJS();
  },
  
  beforeDestroy() {
    // Clean up CollectJS if needed
    if (window.CollectJS) {
      // CollectJS cleanup
    }
  },
  
  methods: {
    prefillBillingInfo() {
      if (this.enrollmentData) {
        const member = this.enrollmentData.primaryMember || {};
        const contact = this.enrollmentData.contactInfo || {};
        
        this.billingInfo.firstName = member.firstName || '';
        this.billingInfo.lastName = member.lastName || '';
        this.billingInfo.address = contact.address || '';
        this.billingInfo.city = contact.city || '';
        this.billingInfo.state = contact.state || '';
        this.billingInfo.zip = contact.zip || '';
        this.billingInfo.phone = contact.phone || '';
      }
    },
    
    loadCollectJS() {
      // Get tokenization key from env
      const tokenKey = process.env.VUE_APP_MIDPAY_TOKENIZATION_KEY;
      
      // Check if key is configured
      if (!tokenKey || tokenKey === 'your-token-key-here') {
        console.warn('⚠️ MidPayGateway tokenization key not configured');
        console.warn('📝 Set VUE_APP_MIDPAY_TOKENIZATION_KEY in client/.env file');
        console.warn('🧪 Enabling TEST MODE - payments will be simulated');
        
        this.errorMessage = '';
        this.successMessage = '';
        
        // Show warning but allow form to work in test mode
        this.$nextTick(() => {
          this.collectJsLoaded = true; // Mark as loaded for test mode
        });
        
        return;
      }
      
      // Check if script already loaded
      if (window.CollectJS) {
        console.log('✅ CollectJS already loaded');
        this.collectJsLoaded = true;
        this.configureCollectJS();
        return;
      }
      
      console.log('🔑 Loading CollectJS with key:', tokenKey.substring(0, 10) + '...');
      
      // Load CollectJS script dynamically
      const script = document.createElement('script');
      script.src = 'https://www.midpaygateway.com/token/Collect.js';
      script.setAttribute('data-tokenization-key', tokenKey);
      
      script.onload = () => {
        console.log('✅ CollectJS script loaded');
        
        // Wait a bit for CollectJS to initialize
        setTimeout(() => {
          if (window.CollectJS) {
            console.log('✅ CollectJS initialized successfully');
            this.collectJsLoaded = true;
            this.configureCollectJS();
          } else {
            console.error('❌ CollectJS object not available after script load');
            this.errorMessage = 'Payment system initialization failed. Please contact support.';
          }
        }, 500);
      };
      
      script.onerror = (error) => {
        console.error('❌ Failed to load CollectJS script:', error);
        this.errorMessage = 'Failed to load payment system. Please check your internet connection and refresh.';
      };
      
      document.head.appendChild(script);
      console.log('📡 CollectJS script tag added to page');
    },
    
    configureCollectJS() {
      if (!window.CollectJS) {
        console.error('❌ CollectJS not available for configuration');
        return;
      }
      
      console.log('⚙️ Configuring CollectJS...');
      
      try {
        // Configure CollectJS
        window.CollectJS.configure({
          variant: 'inline',
          styleSniffer: true,
          invalidCss: {
            'color': '#dc3545'
          },
          validCss: {
            'color': '#28a745'
          },
          customCss: {
            'border': '1px solid rgba(0,0,0,0.38)',
            'border-radius': '4px',
            'padding': '12px',
            'font-size': '16px',
            'font-family': 'Roboto, sans-serif',
            'margin-bottom': '16px'
          },
          focusCss: {
            'border-color': '#1976d2'
          },
          fields: {
            ccnumber: {
              placeholder: 'Card Number',
              selector: '#collect-card-fields'
            },
            ccexp: {
              placeholder: 'MM / YY',
              selector: '#collect-card-fields'
            },
            cvv: {
              placeholder: 'CVV',
              selector: '#collect-card-fields'
            }
          },
          callback: (response) => {
            this.handleCollectJSResponse(response);
          }
        });
        
        console.log('✅ CollectJS configured successfully');
      } catch (error) {
        console.error('❌ Error configuring CollectJS:', error);
        this.errorMessage = 'Error configuring payment system. Please refresh and try again.';
      }
    },
    
    submitPayment() {
      console.log('💳 Submit payment clicked');
      console.log('   Enrollment ID:', this.enrollmentId);
      console.log('   Amount:', this.amount);
      console.log('   Form valid:', this.isFormValid);
      console.log('   CollectJS loaded:', this.collectJsLoaded);
      console.log('   Window.CollectJS available:', !!window.CollectJS);
      console.log('   Token key configured:', !!process.env.VUE_APP_MIDPAY_TOKENIZATION_KEY);
      
      if (this.processing) {
        console.warn('⚠️ Payment already processing');
        return;
      }
      
      this.errorMessage = '';
      this.processing = true;
      
      // Validate form
      if (!this.isFormValid) {
        console.error('❌ Form validation failed');
        console.log('   Billing info:', this.billingInfo);
        this.errorMessage = 'Please fill in all required fields.';
        this.processing = false;
        return;
      }
      
      // TEST MODE: If CollectJS is not configured, simulate payment
      const tokenKey = process.env.VUE_APP_MIDPAY_TOKENIZATION_KEY;
      if (!tokenKey || tokenKey === 'your-token-key-here' || !window.CollectJS) {
        console.warn('🧪 TEST MODE: Simulating payment (no real transaction)');
        console.log('   Reason: tokenKey exists?', !!tokenKey, 'tokenKey valid?', tokenKey !== 'your-token-key-here', 'CollectJS exists?', !!window.CollectJS);
        
        // Simulate a delay
        setTimeout(() => {
          this.handleCollectJSResponse({
            token: 'test-token-' + Date.now(),
            card: {
              number: '411111******1111',
              exp: '12/25',
              type: 'visa'
            }
          });
        }, 1500);
        
        return;
      }
      
      // PRODUCTION MODE: Use real CollectJS
      if (!this.collectJsLoaded) {
        console.error('❌ CollectJS not fully initialized');
        this.errorMessage = 'Payment system is still loading. Please wait a moment and try again.';
        this.processing = false;
        return;
      }
      
      if (!window.CollectJS) {
        console.error('❌ CollectJS object not available');
        this.errorMessage = 'Payment system initialization failed. Please refresh the page and try again.';
        this.processing = false;
        return;
      }
      
      if (!window.CollectJS.startPaymentRequest) {
        console.error('❌ CollectJS.startPaymentRequest method not available');
        this.errorMessage = 'Payment system not properly initialized. Please refresh the page and try again.';
        this.processing = false;
        return;
      }
      
      console.log('✅ Starting payment request with CollectJS');
      
      try {
        // Trigger CollectJS tokenization
        window.CollectJS.startPaymentRequest();
        console.log('✅ startPaymentRequest called successfully');
      } catch (error) {
        console.error('❌ Error starting payment request:', error);
        console.error('   Error details:', error.message, error.stack);
        this.errorMessage = 'Error processing payment. Please try again.';
        this.processing = false;
      }
    },
    
    async handleCollectJSResponse(response) {
      if (response.token) {
        // Token received successfully, now send to backend
        console.log('✅ Payment token received');
        await this.processPaymentWithToken(response.token);
      } else {
        // Tokenization failed
        console.error('❌ Tokenization failed:', response);
        this.errorMessage = response.error || 'Payment processing failed. Please check your card details.';
        this.processing = false;
      }
    },
    
    async processPaymentWithToken(token) {
      try {
        console.log('📤 Sending payment to backend...');
        console.log('   Enrollment ID:', this.enrollmentId);
        console.log('   Amount:', this.amount);
        console.log('   Token:', token.substring(0, 20) + '...');
        
        // Add timeout to prevent hanging
        const timeoutMs = 60000; // 60 seconds timeout
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => {
            reject(new Error('Payment request timed out. Please try again.'));
          }, timeoutMs);
        });
        
        const paymentPromise = axios.post(
          `/api/fhc-enrollments/${this.enrollmentId}/process-payment`,
          {
            paymentToken: token,
            amount: this.amount,
            billingInfo: this.billingInfo
          },
          {
            timeout: timeoutMs
          }
        );
        
        const response = await Promise.race([paymentPromise, timeoutPromise]);
        
        console.log('📥 Backend response:', response.data);
        
        if (response.data.success) {
          this.successMessage = 'Payment processed successfully!';
          console.log('✅ Payment successful!');
          
          // Emit success event after a short delay
          setTimeout(() => {
            this.$emit('payment-success', response.data);
          }, 1500);
        } else {
          throw new Error(response.data.error || 'Payment failed');
        }
      } catch (error) {
        console.error('❌ Payment processing error:', error);
        console.error('   Error response:', error.response?.data);
        
        // Check if it's a timeout or network error
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          this.errorMessage = 'Payment request timed out. Please check your connection and try again.';
        } else if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
          this.errorMessage = 'Unable to connect to payment server. Please check your internet connection and try again.';
        } else {
          // Show detailed error message
          const errorDetails = error.response?.data?.details || '';
          const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || 'Payment processing failed';
          
          this.errorMessage = errorDetails ? `${errorMsg}: ${errorDetails}` : errorMsg;
        }
        
        // Don't block the user - allow retry
        this.processing = false;
        this.$emit('payment-error', error);
      }
    }
  }
};
</script>

<style scoped>
.midpay-collect-form {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

.payment-card {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 12px;
}

.card-fields-container {
  min-height: 150px;
}

.payment-summary {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

/* Test card fields styling - make them look normal, not disabled */
.card-field-test >>> .v-input__slot {
  background-color: #ffffff !important;
}

.card-field-test >>> input {
  color: rgba(0, 0, 0, 0.87) !important;
  font-weight: 500;
  cursor: default;
}

.card-field-test >>> .v-text-field__slot input {
  -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
}

.card-field-test >>> .v-label {
  color: rgba(0, 0, 0, 0.6) !important;
}

.card-field-test >>> .v-icon {
  color: #1976d2 !important;
}

.card-field-test >>> fieldset {
  border-color: rgba(0, 0, 0, 0.38) !important;
}

.card-field-test >>> .v-messages__message {
  color: rgba(0, 0, 0, 0.6) !important;
  font-size: 12px;
}

/* Responsive */
@media (max-width: 600px) {
  .midpay-collect-form {
    padding: 0;
  }
  
  .payment-card {
    border-radius: 0;
    box-shadow: none !important;
  }
}
</style>

